import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Calendar, Download, RefreshCw, Loader2, Globe, Megaphone, ShoppingBag, TrendingUp, TrendingDown, BarChart3, Store, ChevronDown, ChevronRight, ChevronUp, FileDown } from 'lucide-react';
import ConsistencyBadge from '../../components/ConsistencyBadge';
import DatePicker from 'react-datepicker';
import * as XLSX from 'xlsx';
import 'react-datepicker/dist/react-datepicker.css';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { plannerBucket, hasBucketMonth } from './plannerBucket';
import { classifyCampaign } from '../../utils/brandingClassifier';

const API = 'https://api.glowytics.com';
const MERCHANT_NAME_MAPPED_BRAND_IDS = new Set([
  '44e35dfe-0186-495d-a0cc-db5e7e459ab7',
  'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e',
  'dfcf54dd-b00d-434a-be1e-27b6484848eb',
  '7fc8e68d-f2ba-4101-95f7-acece386ab8f',
]);

/* ── Yardımcı ────────────────────────────────────────────────────────── */

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hdr = (brandId) => ({
  'X-Brand-ID': brandId,
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const fmt = (v, f) => {
  if (v == null || v === '') return '–';
  switch (f) {
    case 'currency': return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
    case 'number': return new Intl.NumberFormat('tr-TR').format(v);
    case 'percent': return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
    default: return v;
  }
};

const pctChange = (cur, prev) => {
  if (!prev || prev === 0) return null;
  return ((cur - prev) / prev) * 100;
};

const getLastCompletedMonthRange = () => {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    startDate,
    endDate,
  };
};

const getComparisonPeriods = (startDate, endDate, compareStartDate = null, compareEndDate = null) => {
  const curStart = new Date(startDate);
  const curEnd = new Date(endDate);
  curStart.setHours(0, 0, 0, 0);
  curEnd.setHours(23, 59, 59, 999);

  let prevStart;
  let prevEnd;
  if (compareStartDate && compareEndDate) {
    prevStart = new Date(compareStartDate);
    prevEnd = new Date(compareEndDate);
    prevStart.setHours(0, 0, 0, 0);
    prevEnd.setHours(23, 59, 59, 999);
  } else {
    const periodMs = curEnd.getTime() - curStart.getTime();
    prevEnd = new Date(curStart.getTime() - 1);
    prevStart = new Date(prevEnd.getTime() - periodMs);
  }
  return { curStart, curEnd, prevStart, prevEnd };
};

/* ── Platform rozeti (kampanyanın bağlı olduğu reklam platformu) ───────── */
const PLATFORM_BADGE = {
  'Google Ads': 'bg-blue-50 text-blue-700 ring-blue-200',
  'Meta Ads': 'bg-sky-50 text-sky-700 ring-sky-200',
  'TikTok': 'bg-slate-900 text-white ring-slate-700',
  'Criteo': 'bg-orange-50 text-orange-700 ring-orange-200',
  'RTB': 'bg-violet-50 text-violet-700 ring-violet-200',
  'RTB House': 'bg-violet-50 text-violet-700 ring-violet-200',
  'Pinterest': 'bg-rose-50 text-rose-700 ring-rose-200',
  'Manuel': 'bg-gray-100 text-gray-600 ring-gray-200',
  'Medya Satın Alma': 'bg-amber-50 text-amber-700 ring-amber-200',
};
const platformBadgeClass = (p) => PLATFORM_BADGE[p] || 'bg-gray-100 text-gray-600 ring-gray-200';
function PlatformBadge({ platform }) {
  if (!platform) return null;
  return (
    <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ring-1 ${platformBadgeClass(platform)}`}>
      {platform}
    </span>
  );
}

/* ── Kanal eşleştirme ────────────────────────────────────────────────── */

const CHANNEL_MAP = {
  'Google Ads':           'Google Reklamları',
  'Meta Ads':             'Meta Reklamları',
  'Organic':              'Organik Trafik',
  'Direct':               'Direkt Trafik',
  'Facebook Referral':    'Sosyal Medya Organik',
  'Instagram Referral':   'Sosyal Medya Organik',
  'TikTok Referral':      'Sosyal Medya Organik',
  'Mail':                 'Push - Mailing (Persona)',
  'Push':                 'Push - Mailing (Persona)',
  'SMS':                  'Push - Mailing (Persona)',
  'Email':                'Push - Mailing (Persona)',
  'Newsletter':           'Push - Mailing (Persona)',
};

const CHANNEL_ORDER = [
  'Google Reklamları',
  'Meta Reklamları',
  'Mediabuying',
  'Organik Trafik',
  'Direkt Trafik',
  'Sosyal Medya Organik',
  'Push - Mailing (Persona)',
  'Diğer Reklamlar ve Organik Yönlendirmeler',
];

function mapChannel(apiChannel, source, medium) {
  const src = (source || '').toString().trim().toLowerCase();
  const med = (medium || '').toString().trim().toLowerCase();
  const normalizedSrc = src.replace(/^www\./, '');

  // Explicit business overrides from source/medium mapping.
  // Medya satın alma: ajanslar "mediabuying" ifadesini utm'nin İKİ tarafına da
  // yazıyor — çoğu zaman KAYNAĞA (aditgen_mediabuying / native_banner,
  // sozcu_mediabuying / masthead_banner), bazen ortama. Ayraç da değişiyor
  // (media_buying, "media buying"). Eskiden yalnız ortama ve tam eşitliğe
  // bakılıyordu: Divanev Ağustos 2026'da 2.034 oturumun sadece 31'i bu kovaya
  // düşüyor, kalanı "Diğer"e gidiyordu. Programatik satırları da (cpm/cpv, sadece
  // cpc değil) medya satın almadır.
  const mbNorm = (v) => v.replace(/[\s_\-.]/g, '');
  if (mbNorm(src).includes('mediabuying') || mbNorm(med).includes('mediabuying')) return 'Mediabuying';
  if (mbNorm(src).includes('programmatic') || mbNorm(med).includes('programmatic')) return 'Mediabuying';
  if ((src === 'meta' && med === 'cpc') || (src === 'facebook' && med === 'cpc') || (src === 'fb' && med === 'paid') || (src === 'ig' && med === 'paid') || (src.includes('influencer-ko') && med.includes('reels'))) return 'Meta Reklamları';
  if (src === 'google' && med === 'cpc') return 'Google Reklamları';
  if (src === 'ig' && med === 'social') return 'Sosyal Medya Organik';
  if (med === 'organic') return 'Organik Trafik';
  if ((src === '(direct)' || src === 'direct') && (med === '(none)' || med === 'none')) return 'Direkt Trafik';
  if (
    med === 'referral' &&
    (normalizedSrc === 'facebook.com' || normalizedSrc === 'l.facebook.com' || normalizedSrc === 'instagram.com' || normalizedSrc === 'l.instagram.com')
  ) return 'Sosyal Medya Organik';
  if (src === 'personaclick' && (med === 'web_push' || med === 'bulk_email')) return 'Push - Mailing (Persona)';
  if (src === 'sms' || med.includes('sms')) return 'Push - Mailing (Persona)';
  if (med === 'referral' && (normalizedSrc.includes('app.personaclick.com') || normalizedSrc.includes('personaclick.com'))) return 'Push - Mailing (Persona)';
  if (med.includes('push') || med.includes('web_push') || med.includes('bulk_email') || med.includes('mailing') || med === 'email') return 'Push - Mailing (Persona)';
  if (src.includes('push') || src === 'personaclick' || src === 'mail' || src === 'mailchimp' || src === 'mailing' || src === 'newsletter' || src === 'sms' || src.includes('sms')) return 'Push - Mailing (Persona)';

  if (!apiChannel) return 'Diğer Reklamlar ve Organik Yönlendirmeler';
  if (CHANNEL_MAP[apiChannel]) return CHANNEL_MAP[apiChannel];
  const lc = apiChannel.toLowerCase();
  if (lc.includes('sms') || lc.includes('mail') || lc.includes('email') || lc.includes('push') || lc.includes('newsletter') || lc.includes('mailing')) return 'Push - Mailing (Persona)';
  return 'Diğer Reklamlar ve Organik Yönlendirmeler';
}

/* ── Veri işleme ─────────────────────────────────────────────────────── */

function processChannelData(rawData, startDate, endDate, compareStartDate = null, compareEndDate = null) {
  const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
  const cur = {};
  const prev = {};
  const sourceMediumByChannel = {};

  (rawData || []).forEach(row => {
    const d = new Date(row.month || row.date);
    const ch = mapChannel(row.channel, row.source, row.medium);
    const sessions = Number(row.ga4_sessions ?? row.sessions ?? 0) || 0;
    const revenue = Number(row.ga4_total_revenue ?? row.totalRevenue ?? 0) || 0;
    const source = (row.source || '(not set)').toString().trim() || '(not set)';
    const medium = (row.medium || '(not set)').toString().trim() || '(not set)';
    const sourceMedium = `${source} / ${medium}`;
    if (!sourceMediumByChannel[ch]) sourceMediumByChannel[ch] = {};
    if (!sourceMediumByChannel[ch][sourceMedium]) {
      sourceMediumByChannel[ch][sourceMedium] = {
        name: sourceMedium,
        trafik: 0,
        gelir: 0,
        prevTrafik: 0,
        prevGelir: 0,
      };
    }

    const isCur = d >= curStart && d <= curEnd;
    const isPrev = d >= prevStart && d <= prevEnd;
    if (isCur) {
      if (!cur[ch]) cur[ch] = { trafik: 0, gelir: 0 };
      cur[ch].trafik += sessions;
      cur[ch].gelir  += revenue;
      sourceMediumByChannel[ch][sourceMedium].trafik += sessions;
      sourceMediumByChannel[ch][sourceMedium].gelir += revenue;
    } else if (isPrev) {
      if (!prev[ch]) prev[ch] = { trafik: 0, gelir: 0 };
      prev[ch].trafik += sessions;
      prev[ch].gelir  += revenue;
      sourceMediumByChannel[ch][sourceMedium].prevTrafik += sessions;
      sourceMediumByChannel[ch][sourceMedium].prevGelir += revenue;
    }
  });

  const channels = CHANNEL_ORDER.filter(ch => cur[ch]).map(ch => {
    const c = cur[ch];
    const p = prev[ch] || {};
    const sourceMediums = Object.values(sourceMediumByChannel[ch] || {})
      .filter(s => (s.trafik > 0 || s.gelir > 0 || s.prevTrafik > 0 || s.prevGelir > 0))
      .sort((a, b) => {
        if (b.trafik !== a.trafik) return b.trafik - a.trafik;
        return b.gelir - a.gelir;
      })
      .map(s => ({
        ...s,
        trafikPct: pctChange(s.trafik, s.prevTrafik),
        gelirPct: pctChange(s.gelir, s.prevGelir),
      }));

    return {
      name: ch,
      trafik: c.trafik,
      trafikPct: pctChange(c.trafik, p.trafik),
      gelir: c.gelir,
      gelirPct: pctChange(c.gelir, p.gelir),
      sourceMediums,
    };
  });

  const total = channels.reduce((t, c) => ({ trafik: t.trafik + c.trafik, gelir: t.gelir + c.gelir }), { trafik: 0, gelir: 0 });
  const prevTotal = Object.values(prev).reduce((t, p) => ({ trafik: t.trafik + p.trafik, gelir: t.gelir + p.gelir }), { trafik: 0, gelir: 0 });

  return {
    channels,
    total: {
      trafik: total.trafik,
      trafikPct: pctChange(total.trafik, prevTotal.trafik),
      gelir: total.gelir,
      gelirPct: pctChange(total.gelir, prevTotal.gelir),
    },
  };
}

// Planner'daki "Medya Satınalma" etiketi — report-2 ile aynı normalize mantığı.
// Bu etiketli planner satırları report-3'ün branding kovasına ALINMAZ; aynı para
// report-6'nın media-buying endpoint'inden geldiği için çift sayılırdı.
const normalizeLabel = (v) => (v || '')
  .toString()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim();
const MEDIA_BUYING_LABELS = ['medya satınalma', 'medya satinalma', 'medya satın alma', 'medya satin alma'];

/* ── Medya Satın Alma (manuel) → Branding yardımcıları ─────────────────── */
// TR formatlı sayı parse ("₺1.234,56" -> 1234.56)
const parseTRNum = (str) => {
  if (str == null || str === '' || str === '-' || str === '–') return 0;
  const cleaned = String(str).replace(/[₺\s]/g, '').replace(/\./g, '').replace(/,/g, '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};
// [start,end] aralığındaki tüm (year, month) çiftleri
const monthsInRange = (start, end) => {
  const out = [];
  const d = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (d <= last) {
    out.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
    d.setMonth(d.getMonth() + 1);
  }
  return out;
};
// media-buying-format JSON'undan toplam harcama + satırlar.
// KANONİK / TEK-SAYIM (report-6 normalizer ile aynı kural): Dağılım ve Mecra
// AYNI paranın iki kırılımıdır → yalnızca BİR tablo sayılır. Kanonik kaynak =
// "Mecra" (detay/yayıncı kırılımı); Mecra yoksa "Dağılım"a düşülür. Böylece
// ne çift sayım olur ne de bir tablo eksikse undercount.
const sumMediaBuyingSpend = (json) => {
  const sections = json?.sections || [];
  const isMecra = (t) => (t || '').toLowerCase().includes('mecra');
  // TEK kanonik section seç (Mecra-öncelikli). `.filter().forEach()` yerine tek-seçim:
  // birden çok eşleşen tablo dönse bile çift sayım imkânsız (report-2 ile aynı desen).
  const section = sections.find((s) => isMecra(s?.title)) || sections.find((s) => !isMecra(s?.title)) || sections[0];
  let harcama = 0;
  const rows = [];
  (section?.rows || []).forEach((r) => {
    const b = parseTRNum(r.budget);
    if (b <= 0) return;
    harcama += b;
    rows.push({ name: `Medya Satın Alma | ${r.channel || r.campaign || 'Satır'}`, harcama: b });
  });
  return { harcama, rows };
};

function processPerformanceVsBranding(rawData, startDate, endDate, compareStartDate = null, compareEndDate = null, mediaBuyingBranding = null, tiktokRaw = [], plannerPerformance = null, googleRaw = [], plannerBranding = null, metaObjectives = {}, brandId = null) {
  const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
  const init = () => ({ harcama: 0, trafik: 0, ciro: 0 });
  const curPerf = init(), curBrand = init();
  const prevPerf = init(), prevBrand = init();
  const perfCampaigns = {};
  const brandCampaigns = {};

  const getCostForChannel = (row, ch) => {
    if (!ch) return 0;
    const c = String(ch).toLowerCase();
    if (c.includes('google')) return Number(row.google_cost) || 0;
    if (c.includes('meta')) return Number(row.meta_cost) || 0;
    if (c.includes('criteo')) return Number(row.criteo_cost) || 0;
    if (c.includes('rtb')) return Number(row.rtb_cost) || 0;
    if (c.includes('pinterest')) return Number(row.pinterest_cost) || 0;
    return 0;
  };

  (rawData || []).forEach(row => {
    const d = new Date(row.date || row.month);
    const ch = row.channel;
    // Google artık TAM kaynaktan (campaign-types-all) işleniyor — aşağıdaki googleRaw döngüsü.
    // unified view GA4 join'i session üretmeyen üst-huni Google kampanyalarını düşürdüğü için
    // burada Google'ı atlıyoruz; çift sayımı önler.
    if (ch && String(ch).toLowerCase().includes('google')) return;
    const cost = getCostForChannel(row, ch);
    if (!ch || cost <= 0) return;

    const name = (row.campaign_name || '').toLowerCase();
    const campaignType = (row.campaign_type || '').toString().toLowerCase();
    // 1) Adında açıkça "branding"/"performans" varsa o belirler (her kanal için).
    // 2) Yoksa platformun kendi alanı: Meta'da objective, Google'da kampanya tipi.
    //    unified view Meta satırlarında objective KOLONU YOK — ayrı çekilen
    //    metaObjectives haritasından okunur (bkz. fetchMetaObjectives).
    const isMetaRow = String(ch).toLowerCase().includes('meta');
    const objective = isMetaRow ? metaObjectives[name] : undefined;
    const isBranding = classifyCampaign({
      name,
      platform: isMetaRow ? 'meta' : ch,
      campaignType,
      objective,
      brandId,
    }) === 'branding';
    const sessions = Number(row.ga4_sessions) || 0;
    const revenue = Number(row.ga4_total_revenue) || 0;
    const baseName = (row.campaign_name || ch).toString();
    // Her kampanya adının önüne bağlı olduğu platformu (ch) ekle — Google/Meta dahil.
    const campaignName = `${ch} | ${baseName}`;
    const campaignBucket = isBranding ? brandCampaigns : perfCampaigns;
    if (!campaignBucket[campaignName]) campaignBucket[campaignName] = { name: campaignName, displayName: baseName, platform: ch, cur: init(), prev: init() };

    const isCur = d >= curStart && d <= curEnd;
    const isPrev = d >= prevStart && d <= prevEnd;
    if (isCur) {
      const t = isBranding ? curBrand : curPerf;
      t.harcama += cost; t.trafik += sessions; t.ciro += revenue;
      campaignBucket[campaignName].cur.harcama += cost;
      campaignBucket[campaignName].cur.trafik += sessions;
      campaignBucket[campaignName].cur.ciro += revenue;
    } else if (isPrev) {
      const t = isBranding ? prevBrand : prevPerf;
      t.harcama += cost; t.trafik += sessions; t.ciro += revenue;
      campaignBucket[campaignName].prev.harcama += cost;
      campaignBucket[campaignName].prev.trafik += sessions;
      campaignBucket[campaignName].prev.ciro += revenue;
    }
  });

  // Google kampanyaları — TAM kaynaktan (campaign-types-all / view_google_ads_campaigntype_all).
  // unified view GA4 trafiğine dayalı kurulduğu için session ÜRETMEYEN üst-huni Google
  // kampanyalarını (YouTube/CTV/Display/bazı PMax) düşürüyordu → Kanallar'daki performans/branding
  // harcaması EKSİK çıkıyordu (Yataş Haz'26: ~789K/%17.5 eksik, 237K YouTube tamamen yok).
  // Bu kaynak harcamayı eksiksiz verir + gerçek campaign_type ile sınıflandırma yapılır
  // (unified'da campaign_type kolonu yoktu; Google sınıflandırması yalnız isme bakıyordu).
  (googleRaw || []).forEach(row => {
    const cost = Number(row.googleAdsCost) || 0;
    if (cost <= 0) return;
    const d = new Date(row.date);
    const name = (row.campaignName || '').toLowerCase();
    const campaignType = (row.campaignType || '').toString().toLowerCase();
    const isBranding = classifyCampaign({ name, platform: 'google', campaignType, brandId }) === 'branding';
    const sessions = Number(row.sessions) || 0;
    const revenue = Number(row.ga4Revenue) || 0;
    const baseName = (row.campaignName || 'Google Ads').toString();
    const campaignName = `Google Ads | ${baseName}`;
    const campaignBucket = isBranding ? brandCampaigns : perfCampaigns;
    if (!campaignBucket[campaignName]) campaignBucket[campaignName] = { name: campaignName, displayName: baseName, platform: 'Google Ads', cur: init(), prev: init() };

    const isCur = d >= curStart && d <= curEnd;
    const isPrev = d >= prevStart && d <= prevEnd;
    if (isCur) {
      const t = isBranding ? curBrand : curPerf;
      t.harcama += cost; t.trafik += sessions; t.ciro += revenue;
      campaignBucket[campaignName].cur.harcama += cost;
      campaignBucket[campaignName].cur.trafik += sessions;
      campaignBucket[campaignName].cur.ciro += revenue;
    } else if (isPrev) {
      const t = isBranding ? prevBrand : prevPerf;
      t.harcama += cost; t.trafik += sessions; t.ciro += revenue;
      campaignBucket[campaignName].prev.harcama += cost;
      campaignBucket[campaignName].prev.trafik += sessions;
      campaignBucket[campaignName].prev.ciro += revenue;
    }
  });

  // TikTok kampanyaları — Google/Meta ile AYNI kampanya-adı mantığıyla (nameIsBranding)
  // performans/branding kovalarına ayrılır. Böylece TikTok performans kampanyaları
  // "Performans Reklamları", branding kampanyaları "Branding Reklamları" altında görünür.
  (tiktokRaw || []).forEach(row => {
    const cost = Number(row.spend) || 0;
    if (cost <= 0) return;
    const d = new Date(row.date || row.date_start);
    const isBranding = classifyCampaign({
      name: row.campaign_name,
      platform: 'tiktok',
      objective: row.campaign_objective,
      brandId,
    }) === 'branding';
    const sessions = Number(row.ga4_sessions) || 0;
    const revenue = Number(row.ga4_total_revenue) || 0;
    const baseName = (row.campaign_name || 'TikTok').toString();
    const campaignName = `TikTok | ${baseName}`;
    const campaignBucket = isBranding ? brandCampaigns : perfCampaigns;
    if (!campaignBucket[campaignName]) campaignBucket[campaignName] = { name: campaignName, displayName: baseName, platform: 'TikTok', cur: init(), prev: init() };

    const isCur = d >= curStart && d <= curEnd;
    const isPrev = d >= prevStart && d <= prevEnd;
    if (isCur) {
      const t = isBranding ? curBrand : curPerf;
      t.harcama += cost; t.trafik += sessions; t.ciro += revenue;
      campaignBucket[campaignName].cur.harcama += cost;
      campaignBucket[campaignName].cur.trafik += sessions;
      campaignBucket[campaignName].cur.ciro += revenue;
    } else if (isPrev) {
      const t = isBranding ? prevBrand : prevPerf;
      t.harcama += cost; t.trafik += sessions; t.ciro += revenue;
      campaignBucket[campaignName].prev.harcama += cost;
      campaignBucket[campaignName].prev.trafik += sessions;
      campaignBucket[campaignName].prev.ciro += revenue;
    }
  });

  // Planner'da (report-4) PERFORMANS kovasına manuel girilen kalemler → performans totaline
  // eklenir (GA4 atıfı olmadığı için yalnızca harcama). Branding kovasındakiler buraya GİRMEZ.
  if (plannerPerformance) {
    curPerf.harcama += plannerPerformance.curHarcama || 0;
    prevPerf.harcama += plannerPerformance.prevHarcama || 0;
    (plannerPerformance.curRows || []).forEach((r) => {
      if (!perfCampaigns[r.name]) perfCampaigns[r.name] = { name: r.name, displayName: r.name, platform: 'Manuel', cur: init(), prev: init() };
      perfCampaigns[r.name].cur.harcama += r.harcama || 0;
    });
    (plannerPerformance.prevRows || []).forEach((r) => {
      if (!perfCampaigns[r.name]) perfCampaigns[r.name] = { name: r.name, displayName: r.name, platform: 'Manuel', cur: init(), prev: init() };
      perfCampaigns[r.name].prev.harcama += r.harcama || 0;
    });
  }

  // Planner'da (report-4) BRANDING kovasına manuel girilen kalemler (Yandex, Bing…) →
  // branding totaline eklenir. Kanal kademesinde kendi adıyla görünür (platform = kalem
  // adı), yani report-2'nin Yandex/Pinterest kovalarıyla aynı isimlendirme. Eskiden bu
  // blok yoktu: performans kovası (yukarısı) rapora giriyor, branding kovası hiçbir yere
  // yazılmıyordu → Yandex branding harcaması Kanallar'da HİÇ görünmüyordu.
  // "Medya Satınalma" etiketli kalemler buraya alınmaz; onlar report-6 endpoint'inden
  // aşağıdaki blokla geliyor → çift sayım olmaz (eleme fetchPlannerManual içinde).
  if (plannerBranding) {
    curBrand.harcama += plannerBranding.curHarcama || 0;
    prevBrand.harcama += plannerBranding.prevHarcama || 0;
    (plannerBranding.curRows || []).forEach((r) => {
      if (!brandCampaigns[r.name]) brandCampaigns[r.name] = { name: r.name, displayName: r.name, platform: r.name, cur: init(), prev: init() };
      brandCampaigns[r.name].cur.harcama += r.harcama || 0;
    });
    (plannerBranding.prevRows || []).forEach((r) => {
      if (!brandCampaigns[r.name]) brandCampaigns[r.name] = { name: r.name, displayName: r.name, platform: r.name, cur: init(), prev: init() };
      brandCampaigns[r.name].prev.harcama += r.harcama || 0;
    });
  }

  // Manuel Medya Satın Alma kalemleri birer branding harcamasıdır → branding totaline ekle.
  // (Medya satın almanın GA4 atıfı olmadığı için trafik/ciro'ya değil, yalnızca harcamaya eklenir.)
  if (mediaBuyingBranding) {
    curBrand.harcama += mediaBuyingBranding.curHarcama || 0;
    prevBrand.harcama += mediaBuyingBranding.prevHarcama || 0;
    (mediaBuyingBranding.curRows || []).forEach((r) => {
      if (!brandCampaigns[r.name]) brandCampaigns[r.name] = { name: r.name, displayName: r.name, platform: 'Medya Satın Alma', cur: init(), prev: init() };
      brandCampaigns[r.name].cur.harcama += r.harcama || 0;
    });
    (mediaBuyingBranding.prevRows || []).forEach((r) => {
      if (!brandCampaigns[r.name]) brandCampaigns[r.name] = { name: r.name, displayName: r.name, platform: 'Medya Satın Alma', cur: init(), prev: init() };
      brandCampaigns[r.name].prev.harcama += r.harcama || 0;
    });
  }

  const mapCampaigns = (obj) => Object.values(obj)
    .filter(c => c.cur.harcama > 0 || c.prev.harcama > 0 || c.cur.trafik > 0 || c.prev.trafik > 0 || c.cur.ciro > 0 || c.prev.ciro > 0)
    .map(c => ({
      ...c,
      pct: {
        harcama: pctChange(c.cur.harcama, c.prev.harcama),
        trafik: pctChange(c.cur.trafik, c.prev.trafik),
        ciro: pctChange(c.cur.ciro, c.prev.ciro),
      }
    }))
    .sort((a, b) => b.cur.harcama - a.cur.harcama);

  // Kampanyaları PLATFORMA göre grupla (Performans/Branding altında kanal kademesi).
  // Her grup kendi harcama/trafik/ciro toplamını ve alt kampanyalarını taşır.
  const buildChannelGroups = (obj) => {
    const groups = {};
    mapCampaigns(obj).forEach((c) => {
      const p = c.platform || 'Diğer';
      if (!groups[p]) groups[p] = { platform: p, cur: { harcama: 0, trafik: 0, ciro: 0 }, prev: { harcama: 0, trafik: 0, ciro: 0 }, campaigns: [] };
      const g = groups[p];
      g.cur.harcama += c.cur.harcama; g.cur.trafik += c.cur.trafik; g.cur.ciro += c.cur.ciro;
      g.prev.harcama += c.prev.harcama; g.prev.trafik += c.prev.trafik; g.prev.ciro += c.prev.ciro;
      g.campaigns.push(c);
    });
    return Object.values(groups).map((g) => ({
      platform: g.platform,
      cur: g.cur, prev: g.prev,
      harcama: g.cur.harcama, harcamaPct: pctChange(g.cur.harcama, g.prev.harcama),
      trafik: g.cur.trafik,   trafikPct: pctChange(g.cur.trafik, g.prev.trafik),
      ciro: g.cur.ciro,       ciroPct: pctChange(g.cur.ciro, g.prev.ciro),
      campaigns: g.campaigns,
    })).sort((a, b) => b.harcama - a.harcama);
  };

  return [
    {
      name: 'Performans Reklamları',
      harcama: curPerf.harcama, harcamaPct: pctChange(curPerf.harcama, prevPerf.harcama),
      trafik: curPerf.trafik,   trafikPct: pctChange(curPerf.trafik, prevPerf.trafik),
      ciro: curPerf.ciro,       ciroPct: pctChange(curPerf.ciro, prevPerf.ciro),
      campaigns: mapCampaigns(perfCampaigns),
      channelGroups: buildChannelGroups(perfCampaigns),
    },
    {
      name: 'Branding Reklamları',
      harcama: curBrand.harcama, harcamaPct: pctChange(curBrand.harcama, prevBrand.harcama),
      trafik: curBrand.trafik,   trafikPct: pctChange(curBrand.trafik, prevBrand.trafik),
      ciro: curBrand.ciro,       ciroPct: pctChange(curBrand.ciro, prevBrand.ciro),
      campaigns: mapCampaigns(brandCampaigns),
      channelGroups: buildChannelGroups(brandCampaigns),
    },
  ];
}

/* Sabit kategorili markalar: listedeki 3 kategori bu markada her zaman ve bu sırayla ilk üç
   kart olur. Kalan slotlar ciroya göre en büyük diğer kategorilerle dolar (en fazla 5 kart).
   Kategori adları GA4 item_category değerleridir; eşleşme normalizeCategoryKey ile yapılır. */
const PINNED_CATEGORIES_BY_BRAND = {
  // Yataş Bedding
  '44e35dfe-0186-495d-a0cc-db5e7e459ab7': ['Yataklar', 'Ev Tekstili', 'Yastık & Yorgan'],
  // puffy
  '7fc8e68d-f2ba-4101-95f7-acece386ab8f': ['Yatak', 'Uyku Tekstili', 'Yastık & Yorgan & Alez'],
};
const PINNED_MAX_CATEGORY_CARDS = 5;
const DEFAULT_MAX_CATEGORY_CARDS = 3;
const normalizeCategoryKey = (v) => String(v || '').toLocaleLowerCase('tr-TR').replace(/\s+/g, ' ').trim();

function processProducts(products, sortConfig = { key: 'adet', direction: 'desc' }, brandId = '') {
  const byCategory = {};
  (products || []).forEach(p => {
    const cat = p.itemCategory || 'Diğer';
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(p);
  });

  const key = sortConfig?.key || 'adet';
  const direction = sortConfig?.direction === 'asc' ? 'asc' : 'desc';
  const sortValue = (item) => {
    if (key === 'gelir') return Number(item.totalRevenue) || 0;
    return Number(item.totalItemsPurchased) || 0;
  };
  const directionFactor = direction === 'asc' ? 1 : -1;

  const byRevenue = Object.entries(byCategory)
    .map(([cat, items]) => ({
      category: cat,
      totalRevenue: items.reduce((s, i) => s + (Number(i.totalRevenue) || 0), 0),
      items: items.sort((a, b) => (sortValue(a) - sortValue(b)) * directionFactor).slice(0, 25),
    }))
    .sort((a, b) => b.totalRevenue - a.totalRevenue);

  // Sabit kategorisi tanımlı olmayan markalarda davranış aynı: ciroya göre ilk 3 kategori
  const pinnedCategories = PINNED_CATEGORIES_BY_BRAND[brandId];
  if (!pinnedCategories) return byRevenue.slice(0, DEFAULT_MAX_CATEGORY_CARDS);

  const pinnedOrder = new Map(pinnedCategories.map((c, i) => [normalizeCategoryKey(c), i]));
  const isPinned = (c) => pinnedOrder.has(normalizeCategoryKey(c.category));
  const pinned = byRevenue
    .filter(isPinned)
    .sort((a, b) => pinnedOrder.get(normalizeCategoryKey(a.category)) - pinnedOrder.get(normalizeCategoryKey(b.category)));
  const rest = byRevenue.filter(c => !isPinned(c));

  return [...pinned, ...rest].slice(0, PINNED_MAX_CATEGORY_CARDS);
}

/* ── Kategori performans (dönemsel karşılaştırma) ────────────────────── */

function processCategoryComparison(monthlyData, startDate, endDate, compareStartDate = null, compareEndDate = null) {
  const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
  const fmtPeriod = (s, e) => {
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${s.toLocaleDateString('tr-TR', opts)} - ${e.toLocaleDateString('tr-TR', opts)}`;
  };
  const curLabel = fmtPeriod(curStart, curEnd);
  const prevLabel = fmtPeriod(prevStart, prevEnd);

  const init = () => ({ views: 0, addToCart: 0, purchases: 0, revenue: 0 });

  const curCats = {};
  const prevCats = {};

  const rows = Array.isArray(monthlyData) ? monthlyData : monthlyData?.data || [];

  rows.forEach(row => {
    const d = new Date(row.date || row.day || row.monthStart || `${row.year}-${String(row.month).padStart(2, '0')}-01`);
    const cat = row.itemCategory || 'Diğer';
    const isCurPeriod = d >= curStart && d <= curEnd;
    const isPrevPeriod = d >= prevStart && d <= prevEnd;
    if (!isCurPeriod && !isPrevPeriod) return;

    const target = isCurPeriod ? curCats : prevCats;
    if (!target[cat]) target[cat] = init();
    target[cat].views     += Number(row.totalItemsViewed)       || 0;
    target[cat].addToCart += Number(row.totalItemsAddedToCart || 0);
    target[cat].purchases += Number(row.totalItemsPurchased)     || 0;
    target[cat].revenue   += Number(row.totalRevenue)            || 0;
  });

  const allCats = [...new Set([...Object.keys(curCats), ...Object.keys(prevCats)])];
  const sorted = allCats
    .map(cat => ({ cat, rev: (curCats[cat]?.revenue || 0) + (prevCats[cat]?.revenue || 0) }))
    .sort((a, b) => b.rev - a.rev);

  const curTotal = init();
  const prevTotal = init();
  Object.values(curCats).forEach(c => { curTotal.views += c.views; curTotal.addToCart += c.addToCart; curTotal.purchases += c.purchases; curTotal.revenue += c.revenue; });
  Object.values(prevCats).forEach(p => { prevTotal.views += p.views; prevTotal.addToCart += p.addToCart; prevTotal.purchases += p.purchases; prevTotal.revenue += p.revenue; });

  const buildRow = (cat, cur, prev) => {
    const c = cur || init();
    const p = prev || init();
    return {
      category: cat,
      curLabel,
      prevLabel,
      cur: c,
      prev: p,
      pct: {
        views: pctChange(c.views, p.views),
        addToCart: pctChange(c.addToCart, p.addToCart),
        purchases: pctChange(c.purchases, p.purchases),
        revenue: pctChange(c.revenue, p.revenue),
      },
    };
  };

  return [
    buildRow('TOPLAM', curTotal, prevTotal),
    ...sorted.map(s => buildRow(s.cat, curCats[s.cat], prevCats[s.cat])),
  ];
}

/* ── Ürün düşüş karşılaştırması (aylık) ─────────────────────────────── */
function processProductDropComparison(monthlyItemsData, startDate, endDate, compareStartDate = null, compareEndDate = null) {
  const rows = Array.isArray(monthlyItemsData) ? monthlyItemsData : monthlyItemsData?.data || [];
  if (!rows.length) return { curLabel: '', prevLabel: '', items: [] };

  const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
  const fmtPeriod = (s, e) => {
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${s.toLocaleDateString('tr-TR', opts)} - ${e.toLocaleDateString('tr-TR', opts)}`;
  };
  const curLabel = fmtPeriod(curStart, curEnd);
  const prevLabel = fmtPeriod(prevStart, prevEnd);

  const byProduct = new Map();
  for (const r of rows) {
    const y = Number(r.year || 0);
    const m = Number(r.month || 0);
    const d = new Date(r.date || r.day || `${y}-${String(m).padStart(2, '0')}-01`);
    const isCur = d >= curStart && d <= curEnd;
    const isPrev = d >= prevStart && d <= prevEnd;
    if (!isCur && !isPrev) continue;

    const key = r.itemId || r.itemName || `unknown-${y}-${m}`;
    if (!byProduct.has(key)) {
      byProduct.set(key, {
        itemId: r.itemId || r.item_id || null,
        itemName: r.itemName || 'Unknown Product',
        itemCategory: r.itemCategory || 'Diğer',
        cur: { revenue: 0, purchased: 0, viewed: 0 },
        prev: { revenue: 0, purchased: 0, viewed: 0 },
      });
    }
    const entry = byProduct.get(key);
    const target = isCur ? entry.cur : entry.prev;
    target.revenue += Number(r.totalRevenue || 0);
    target.purchased += Number(r.totalItemsPurchased || 0);
    target.viewed += Number(r.totalItemsViewed || 0);
  }

  const MIN_PREV_PURCHASED = 10;
  const items = Array.from(byProduct.values())
    .map((p) => {
      const revenuePct = pctChange(p.cur.revenue, p.prev.revenue);
      const purchasedPct = pctChange(p.cur.purchased, p.prev.purchased);
      const viewedPct = pctChange(p.cur.viewed, p.prev.viewed);
      const absDropped = p.prev.purchased - p.cur.purchased;
      return { ...p, pct: { revenue: revenuePct, purchased: purchasedPct, viewed: viewedPct }, absDropped };
    })
    .filter((p) => p.prev.purchased >= MIN_PREV_PURCHASED && p.cur.purchased < p.prev.purchased && p.pct.purchased != null)
    .sort((a, b) => b.absDropped - a.absDropped || a.pct.purchased - b.pct.purchased);

  return { curLabel, prevLabel, items };
}

/* ── ikas veri işleme ────────────────────────────────────────────────── */

function processIkasData(curRows, prevRows) {
  const aggregate = (rows) => (rows || []).reduce((acc, r) => {
    acc.revenue += Number(r.total_price || r.revenue || 0);
    acc.orders  += Number(r.order_count || r.orders_count || 0);
    acc.items   += Number(r.items_count || 0);
    return acc;
  }, { revenue: 0, orders: 0, items: 0 });

  const cur = aggregate(curRows);
  const prev = aggregate(prevRows);

  return {
    cur,
    prev,
    pct: {
      revenue: pctChange(cur.revenue, prev.revenue),
      orders: pctChange(cur.orders, prev.orders),
      items: pctChange(cur.items, prev.items),
    },
    dailyRows: (curRows || [])
      .map(r => ({
        date: r.day || r.date,
        revenue: Number(r.total_price || r.revenue || 0),
        orders: Number(r.order_count || r.orders_count || 0),
        items: Number(r.items_count || 0),
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date)),
  };
}

/* ── Bileşenler ──────────────────────────────────────────────────────── */

function CatPctCell({ value }) {
  if (value == null) return <span className="text-[10px] text-slate-300">–</span>;
  const isPos = value >= 0;
  const bg = isPos ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums ${bg}`}>
      {value >= 0 ? '+' : ''}{value.toFixed(2)}%
    </span>
  );
}

function PctBadge({ value }) {
  if (value == null) return <span className="text-slate-300">–</span>;
  const isPos = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${isPos ? 'text-emerald-600' : 'text-red-500'}`}>
      {isPos ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
      {fmt(value, 'percent')}
    </span>
  );
}

// `busy`: bu bölümün KENDİ verisi henüz gelmedi. Sayfa tamamının yüklenmesini
// beklemek yerine her bölüm hazır olunca açılır; bekleyen bölüm iskelet gösterir.
function Section({ icon: Icon, title, busy = false, children }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
          <Icon size={14} className="text-slate-300" />
        </div>
        <h2 className="text-[13px] font-bold text-slate-800">{title}</h2>
        {busy && <Loader2 size={12} className="text-slate-400 animate-spin" />}
      </div>
      {busy ? (
        <div className="rounded-lg border border-slate-200/80 bg-white p-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="mb-2 h-6 animate-pulse rounded bg-slate-100 last:mb-0" />
          ))}
        </div>
      ) : children}
    </div>
  );
}

// Bölümlerin beklediği veri kümeleri — fetchAll ile bire bir aynı kalmalı.
const TUM_ISLER = ['kanal', 'perf', 'google', 'metaObj', 'mb', 'tiktok', 'planner', 'urun', 'gunluk', 'ikas'];

/* ── Ana bileşen ────────────────────────────────────────────────────── */

export default function CustomReport3() {
  const activeBrandId = localStorage.getItem('currentBrandId') || '';
  const showOnlyItemIdInTopProducts = MERCHANT_NAME_MAPPED_BRAND_IDS.has(activeBrandId);
  const [startDate, setStartDate] = useState(() => getLastCompletedMonthRange().startDate);
  const [endDate, setEndDate] = useState(() => getLastCompletedMonthRange().endDate);
  const [pendingStartDate, setPendingStartDate] = useState(() => getLastCompletedMonthRange().startDate);
  const [pendingEndDate, setPendingEndDate] = useState(() => getLastCompletedMonthRange().endDate);
  const initialAutoCompare = useMemo(() => {
    const base = getLastCompletedMonthRange();
    return getComparisonPeriods(base.startDate, base.endDate);
  }, []);
  const [compareStartDate, setCompareStartDate] = useState(() => initialAutoCompare.prevStart);
  const [compareEndDate, setCompareEndDate] = useState(() => initialAutoCompare.prevEnd);
  const [pendingCompareStartDate, setPendingCompareStartDate] = useState(() => initialAutoCompare.prevStart);
  const [pendingCompareEndDate, setPendingCompareEndDate] = useState(() => initialAutoCompare.prevEnd);
  const [isCompareAuto, setIsCompareAuto] = useState(true);
  const [showComparePicker, setShowComparePicker] = useState(false);
  const [loading, setLoading] = useState(false);
  // Hangi veri kümeleri hâlâ yolda. Bölümler buna bakarak tek tek açılır.
  // DOLU başlar: boş başlarsa ilk render'da bölümler bir an BOŞ tablo gösterip
  // sonra iskelete geçiyor, göz kırpması gibi duruyordu.
  const [pending, setPending] = useState(() => new Set(TUM_ISLER));
  const bekliyor = useCallback((...keys) => keys.some((k) => pending.has(k)), [pending]);
  const pdfRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExportPDF = useCallback(async () => {
    if (exporting || !pdfRef.current) return;
    setExporting(true);
    try {
      const el = pdfRef.current;
      const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: 1400, letterRendering: true, allowTaint: true });
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;
      const imgW = usableW;
      const imgH = (canvas.height * usableW) / canvas.width;
      let y = 0;
      let page = 0;
      while (y < imgH) {
        if (page > 0) pdf.addPage();
        const srcY = (y / imgH) * canvas.height;
        const srcH = Math.min((usableH / imgH) * canvas.height, canvas.height - srcY);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcH;
        sliceCanvas.getContext('2d').drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.92);
        const sliceH = (srcH * usableW) / canvas.width;
        pdf.addImage(sliceData, 'JPEG', margin, margin, imgW, sliceH);
        y += usableH;
        page++;
      }
      pdf.save('Aylik_Performans_Raporu.pdf');
    } catch (err) { console.error('PDF export failed:', err); }
    finally { setExporting(false); }
  }, [exporting]);

  const [channelRaw, setChannelRaw] = useState([]);
  const [googleRaw, setGoogleRaw] = useState([]);
  const [metaObjectives, setMetaObjectives] = useState({});
  // Sınıflandırma kuralları marka bazlı (hepsi-branding markaları için) — her render'da okunur.
  const brandIdForRules = localStorage.getItem('currentBrandId');
  const [mediaBuyingBranding, setMediaBuyingBranding] = useState(null);
  const [gaSourceMediumRaw, setGaSourceMediumRaw] = useState([]);
  const [products, setProducts] = useState([]);
  const [categoryRaw, setCategoryRaw] = useState([]);
  const [monthlyItemRaw, setMonthlyItemRaw] = useState([]);
  const [itemNameById, setItemNameById] = useState({});
  const [expandedChannelRows, setExpandedChannelRows] = useState({});
  const [expandedPerfRows, setExpandedPerfRows] = useState({});
  const [expandedPerfChannels, setExpandedPerfChannels] = useState({});
  const [topProductsSort, setTopProductsSort] = useState({ key: 'adet', direction: 'desc' });

  // TikTok ham veri — Google/Meta gibi kampanya-adına göre perf/branding ayrılır.
  const [tiktokRaw, setTiktokRaw] = useState([]);
  // Planner (report-4) performans kovasına manuel girilen kalemler.
  const [plannerPerformance, setPlannerPerformance] = useState(null);
  // Planner (report-4) branding kovasına manuel girilen kalemler (Yandex, Bing…).
  const [plannerBranding, setPlannerBranding] = useState(null);

  // ikas states
  const [hasIkasConnected, setHasIkasConnected] = useState(false);
  const [ikasLoading, setIkasLoading] = useState(false);
  const [ikasCurRows, setIkasCurRows] = useState([]);
  const [ikasPrevRows, setIkasPrevRows] = useState([]);

  const fetchChannelData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
      const fetchStart = curStart < prevStart ? curStart : prevStart;
      const fetchEnd = curEnd > prevEnd ? curEnd : prevEnd;
      const params = new URLSearchParams({
        dateRange: 'custom',
        startDate: fmtDate(fetchStart),
        endDate: fmtDate(fetchEnd),
      });
      const res = await fetch(`${API}/api/google-ads/unified-campaign-performance?${params}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) return;
      setChannelRaw(await res.json());
    } catch (e) { console.error('Channel data:', e); }
  }, [startDate, endDate, compareStartDate, compareEndDate]);

  // Google TAM harcama kaynağı (campaign-types-all). Kanallar bölümündeki performans/branding
  // ayrımı bunu kullanır — unified view'in düşürdüğü üst-huni Google kampanyalarını da içerir.
  const fetchGoogleComplete = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
      const fetchStart = curStart < prevStart ? curStart : prevStart;
      const fetchEnd = curEnd > prevEnd ? curEnd : prevEnd;
      const params = new URLSearchParams({
        dateRange: 'custom',
        startDate: fmtDate(fetchStart),
        endDate: fmtDate(fetchEnd),
      });
      const res = await fetch(`${API}/api/google-ads/campaign-types-all?${params}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) { setGoogleRaw([]); return; }
      const json = await res.json();
      setGoogleRaw(Array.isArray(json?.rawData) ? json.rawData : []);
    } catch (e) { console.error('Google complete data:', e); setGoogleRaw([]); }
  }, [startDate, endDate, compareStartDate, compareEndDate]);

  // Meta kampanya objective'leri. Kanallar bölümündeki Meta harcaması unified view'dan
  // geliyor ama o view objective KOLONU taşımıyor — adında "Branding"/"Performans"
  // geçmeyen markalarda (ör. Divanev) her Meta kampanyası performansa düşüyordu.
  // Burada ad → objective haritası kurulur; harcama rakamına dokunulmaz.
  const fetchMetaObjectives = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
      const fetchStart = curStart < prevStart ? curStart : prevStart;
      const fetchEnd = curEnd > prevEnd ? curEnd : prevEnd;
      const params = new URLSearchParams({
        dateRange: 'custom',
        startDate: fmtDate(fetchStart),
        endDate: fmtDate(fetchEnd),
      });
      const res = await fetch(`${API}/api/google-ads/unified-meta-ga4-performance?${params}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) { setMetaObjectives({}); return; }
      const json = await res.json();
      const rows = Array.isArray(json) ? json : (Array.isArray(json?.rawData) ? json.rawData : []);
      const map = {};
      rows.forEach((r) => {
        const n = (r.campaign_name || r.campaignName || '').toString().toLowerCase();
        const o = r.campaign_objective || r.campaignObjective;
        if (n && o && !map[n]) map[n] = o;
      });
      setMetaObjectives(map);
    } catch (e) { console.error('Meta objectives:', e); setMetaObjectives({}); }
  }, [startDate, endDate, compareStartDate, compareEndDate]);

  // Manuel Medya Satın Alma verisini çek (report-6'da kaydedilen) ve branding harcamasına ekle.
  // Endpoint sadece izinli brand'lerde (REPORT6_ALLOWED_BRAND_IDS) 200 döner; diğerlerinde 403 → null kalır, rapor değişmez.
  const fetchMediaBuyingBranding = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
      const fetchPeriod = async (months) => {
        let harcama = 0; const rows = [];
        for (const { year, month } of months) {
          try {
            const r = await fetch(`${API}/api/brands/${brandId}/media-buying-format?year=${year}&month=${month}`, { credentials: 'include', headers: hdr(brandId) });
            if (!r.ok) continue;
            const s = sumMediaBuyingSpend(await r.json());
            harcama += s.harcama; rows.push(...s.rows);
          } catch (_) { /* tek ay hatası raporu bozmasın */ }
        }
        return { harcama, rows };
      };
      const cur = await fetchPeriod(monthsInRange(curStart, curEnd));
      const prev = await fetchPeriod(monthsInRange(prevStart, prevEnd));
      if (cur.harcama === 0 && prev.harcama === 0) { setMediaBuyingBranding(null); return; }
      setMediaBuyingBranding({ curHarcama: cur.harcama, prevHarcama: prev.harcama, curRows: cur.rows, prevRows: prev.rows });
    } catch (e) { console.error('Media buying branding:', e); setMediaBuyingBranding(null); }
  }, [startDate, endDate, compareStartDate, compareEndDate]);

  // Digital Channels source/medium data source:
  // Keep this aligned with analytics/source-medium-monthly page.
  const fetchGaSourceMediumData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
      const fetchStart = curStart < prevStart ? curStart : prevStart;
      const fetchEnd = curEnd > prevEnd ? curEnd : prevEnd;
      const params = new URLSearchParams({
        dateRange: 'custom',
        startDate: fmtDate(fetchStart),
        endDate: fmtDate(fetchEnd),
      });
      const res = await fetch(`${API}/api/google-ads/ga-traffic-source?${params}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) return;
      const js = await res.json().catch(() => ({}));
      setGaSourceMediumRaw(Array.isArray(js?.detailedData) ? js.detailedData : []);
    } catch (e) { console.error('GA source medium data:', e); }
  }, [startDate, endDate, compareStartDate, compareEndDate]);

  const fetchProducts = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const params = new URLSearchParams({ dateRange: 'last30', limit: '100', sortBy: 'total_items_purchased', sortOrder: 'DESC' });
      const res = await fetch(`${API}/api/google-ads/item-performance-summary?${params}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) return;
      setProducts(await res.json());
    } catch (e) { console.error('Products:', e); }
  }, []);

  const fetchDailyItemComparisonData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const { prevStart, prevEnd, curStart, curEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
      const fetchStart = curStart < prevStart ? curStart : prevStart;
      const fetchEnd = curEnd > prevEnd ? curEnd : prevEnd;
      const params = new URLSearchParams({
        dateRange: 'custom',
        startDate: fmtDate(fetchStart),
        endDate: fmtDate(fetchEnd),
      });
      const res = await fetch(`${API}/api/google-ads/daily-item-performance?${params}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) return;
      const js = await res.json().catch(() => ({}));
      let rows = Array.isArray(js) ? js : (js?.data || []);

      const rowDate = (row) => new Date(
        row?.date || row?.day || row?.monthStart || `${row?.year}-${String(row?.month || '').padStart(2, '0')}-01`
      );
      const inRange = (d, s, e) => d instanceof Date && !Number.isNaN(d.getTime()) && d >= s && d <= e;

      // Backward-compatible fallback:
      // Production may ignore custom dateRange and return only recent days.
      // Avoid using dateRange=all (can be very heavy / blocked by gateway), use last90.
      const hasPrevPeriodRows = rows.some((row) => inRange(rowDate(row), prevStart, prevEnd));
      const hasCurPeriodRows = rows.some((row) => inRange(rowDate(row), curStart, curEnd));

      if (!hasPrevPeriodRows || !hasCurPeriodRows) {
        const fallbackRes = await fetch(`${API}/api/google-ads/daily-item-performance?dateRange=last90`, {
          credentials: 'include',
          headers: hdr(brandId),
        });
        if (fallbackRes.ok) {
          const fallbackJs = await fallbackRes.json().catch(() => ({}));
          const fallbackRows = Array.isArray(fallbackJs) ? fallbackJs : (fallbackJs?.data || []);
          rows = fallbackRows.filter((row) => inRange(rowDate(row), fetchStart, fetchEnd));
        }
      }

      setMonthlyItemRaw(rows);
      setCategoryRaw(rows);
    } catch (e) {
      console.error('Daily item comparison data:', e);
    }
  }, [startDate, endDate, compareStartDate, compareEndDate]);

  // ikas connection check + data fetch
  const fetchIkasData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const headers = hdr(brandId);

      // Check connection
      const connRes = await fetch(`${API}/api/auth/connections`, { credentials: 'include', headers });
      if (!connRes.ok) return;
      const connData = await connRes.json();
      let connected = false;
      if (Array.isArray(connData)) {
        connected = !!connData.find(c => String(c.platform || '').toUpperCase() === 'IKAS' && String(c.status || '').toUpperCase() === 'ACTIVE');
      } else if (connData && typeof connData === 'object') {
        const ikasObj = connData.IKAS || connData.ikas;
        connected = !!(ikasObj && String(ikasObj.status || '').toUpperCase() === 'ACTIVE');
      }
      setHasIkasConnected(connected);
      if (!connected) {
        setIkasCurRows([]);
        setIkasPrevRows([]);
        return;
      }

      setIkasLoading(true);

      const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
      // Current period
      const curParams = new URLSearchParams({ startDate: fmtDate(curStart), endDate: fmtDate(curEnd) });
      const curRes = await fetch(`${API}/api/ikas/orders/daily?${curParams}`, { credentials: 'include', headers });
      const curData = curRes.ok ? await curRes.json().catch(() => ({})) : {};
      const curRows = Array.isArray(curData) ? curData : (curData?.rows || curData?.data || []);
      setIkasCurRows(curRows);

      // Compare period
      const prevParams = new URLSearchParams({ startDate: fmtDate(prevStart), endDate: fmtDate(prevEnd) });
      const prevRes = await fetch(`${API}/api/ikas/orders/daily?${prevParams}`, { credentials: 'include', headers });
      const prevData = prevRes.ok ? await prevRes.json().catch(() => ({})) : {};
      const prevRows = Array.isArray(prevData) ? prevData : (prevData?.rows || prevData?.data || []);
      setIkasPrevRows(prevRows);
    } catch (e) {
      console.error('ikas data:', e);
    } finally {
      setIkasLoading(false);
    }
  }, [startDate, endDate, compareStartDate, compareEndDate]);

  const fetchTikTokData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      // channelRaw ile aynı pencereyi çek; perf/branding ayrımı ve cur/prev kovalama
      // processPerformanceVsBranding içinde kampanya adına göre yapılır.
      const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
      const fetchStart = curStart < prevStart ? curStart : prevStart;
      const fetchEnd = curEnd > prevEnd ? curEnd : prevEnd;
      const params = new URLSearchParams({ startDate: fmtDate(fetchStart), endDate: fmtDate(fetchEnd) });
      const res = await fetch(`${API}/api/tiktok-ads/unified-campaign-performance?${params}`, {
        credentials: 'include',
        headers: hdr(brandId),
      });
      if (!res.ok) { setTiktokRaw([]); return; }
      const rows = await res.json();
      setTiktokRaw(Array.isArray(rows) ? rows : []);
    } catch (err) { console.error('TikTok fetch error:', err); setTiktokRaw([]); }
  }, [startDate, endDate, compareStartDate, compareEndDate]);

  // Planner'dan (report-4) manuel kalemleri çek; cur/prev dönemlere gün-örtüşme oranıyla
  // dağıt. Sistem satırları (google/meta performance/branding) canlı veriden geldiği için
  // DIŞLANIR — çift sayma olmaz. Tek fetch ile İKİ kova da doldurulur: performans kovası
  // "Performans Reklamları"na, branding kovası "Branding Reklamları"na yazılır.
  const fetchPlannerManual = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) { setPlannerPerformance(null); setPlannerBranding(null); return; }
      const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
      const years = Array.from(new Set([curStart, curEnd, prevStart, prevEnd].map((d) => d.getFullYear())));
      const responses = await Promise.all(years.map(async (year) => {
        const res = await fetch(`${API}/api/brands/${brandId}/monthly-target-plans?year=${year}`, { credentials: 'include', headers: hdr(brandId) });
        if (!res.ok) return { year, rows: [] };
        const json = await res.json().catch(() => ({}));
        return { year, rows: json?.rows || [] };
      }));
      const overlapDays = (aS, aE, bS, bE) => { const s = aS > bS ? aS : bS; const e = aE < bE ? aE : bE; if (e < s) return 0; return Math.floor((e - s) / 86400000) + 1; };
      const sumForRange = (bucket, rangeStart, rangeEnd, fallbackName) => {
        const byName = {};
        let total = 0;
        responses.forEach(({ year, rows: planRows }) => {
          (planRows || []).forEach((r) => {
            if ((r.metricType || '').toLowerCase() !== 'spend') return;
            const key = (r.key || '').toString();
            if (r.isSystem) return;                    // sistem satırları canlı veriden gelir
            // Branding tarafında "Medya Satınalma" kalemleri atlanır: report-3 bu parayı
            // report-6'nın media-buying endpoint'inden ayrıca çekiyor (mediaBuyingBranding).
            if (bucket === 'branding' && MEDIA_BUYING_LABELS.includes(normalizeLabel(r.label))) return;
            // Kova artık AY BAZLI: satır anahtarı `_branding` olsa bile o ay için
            // performans işaretlenmişse performans kovasına girer (Yandex/Bing gibi ay ay
            // yön değiştiren elle girilen kanallar için). Alan boşsa anahtara düşer.
            const bucketOf = (m) => plannerBucket(r, m);
            // Satırın hiçbir ayı bu kovada değilse boşuna dolaşma.
            if (!hasBucketMonth(r, bucket)) return;
            let harcama = 0;
            for (let m = 1; m <= 12; m++) {
              if (bucketOf(m) !== bucket) continue;
              const mS = new Date(year, m - 1, 1); mS.setHours(0, 0, 0, 0);
              const mE = new Date(year, m, 0); mE.setHours(23, 59, 59, 999);
              const ov = overlapDays(rangeStart, rangeEnd, mS, mE);
              if (ov <= 0) continue;
              const dim = new Date(year, m, 0).getDate();
              const raw = Number(r.values?.[m] ?? r.values?.[String(m)] ?? 0) || 0;
              harcama += raw * (ov / dim);
            }
            if (harcama > 0) {
              const name = (r.label || key).toString().trim() || fallbackName;
              byName[name] = (byName[name] || 0) + harcama;
              total += harcama;
            }
          });
        });
        return { total, rows: Object.entries(byName).map(([name, harcama]) => ({ name, harcama })) };
      };
      const apply = (bucket, fallbackName, setter) => {
        const cur = sumForRange(bucket, curStart, curEnd, fallbackName);
        const prev = sumForRange(bucket, prevStart, prevEnd, fallbackName);
        if (cur.total === 0 && prev.total === 0) { setter(null); return; }
        setter({ curHarcama: cur.total, prevHarcama: prev.total, curRows: cur.rows, prevRows: prev.rows });
      };
      apply('performance', 'Manuel Performans', setPlannerPerformance);
      apply('branding', 'Manuel Branding', setPlannerBranding);
    } catch (e) { console.error('Planner manual:', e); setPlannerPerformance(null); setPlannerBranding(null); }
  }, [startDate, endDate, compareStartDate, compareEndDate]);

  const fetchAll = useCallback(async () => {
    // Eskiden Promise.all bitene kadar TÜM sayfa engelleniyordu: en yavaş uç
    // (daily-item-performance ~1,8 sn) hazır olana dek 131 ms'de gelen kanal
    // tabloları da görünmüyordu. Artık her iş bitince kendi anahtarını düşürür,
    // ilgili bölüm o anda açılır.
    const isler = [
      ['kanal', fetchGaSourceMediumData],
      ['perf', fetchChannelData], ['google', fetchGoogleComplete],
      ['metaObj', fetchMetaObjectives], ['mb', fetchMediaBuyingBranding],
      ['tiktok', fetchTikTokData], ['planner', fetchPlannerManual],
      ['urun', fetchProducts], ['gunluk', fetchDailyItemComparisonData],
      ['ikas', fetchIkasData],
    ];
    setPending(new Set(isler.map(([k]) => k)));
    setLoading(true);
    await Promise.all(isler.map(([k, fn]) =>
      Promise.resolve()
        .then(fn)
        .catch((e) => { console.error(`${k}:`, e); })
        .finally(() => setPending((prev) => { const n = new Set(prev); n.delete(k); return n; }))
    ));
    setLoading(false);
  }, [fetchChannelData, fetchGoogleComplete, fetchMetaObjectives, fetchMediaBuyingBranding, fetchGaSourceMediumData, fetchProducts, fetchDailyItemComparisonData, fetchIkasData, fetchTikTokData, fetchPlannerManual]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ── Hesaplamalar ──────────────────────────────────────────────────── */

  const perfVsBrand = useMemo(
    () => processPerformanceVsBranding(channelRaw, startDate, endDate, compareStartDate, compareEndDate, mediaBuyingBranding, tiktokRaw, plannerPerformance, googleRaw, plannerBranding, metaObjectives, brandIdForRules),
    [channelRaw, startDate, endDate, compareStartDate, compareEndDate, mediaBuyingBranding, tiktokRaw, plannerPerformance, googleRaw, plannerBranding, metaObjectives, brandIdForRules]
  );
  const channelData = useMemo(
    () => processChannelData(gaSourceMediumRaw, startDate, endDate, compareStartDate, compareEndDate),
    [gaSourceMediumRaw, startDate, endDate, compareStartDate, compareEndDate]
  );
  const channelMonthInfo = useMemo(() => {
    const { curStart, curEnd, prevStart, prevEnd } = getComparisonPeriods(startDate, endDate, compareStartDate, compareEndDate);
    const monthLabel = (d) => d.toLocaleDateString('tr-TR', { month: 'short', day: 'numeric', year: 'numeric' });
    return {
      curLabel: `${monthLabel(curStart)} - ${monthLabel(curEnd)}`,
      prevLabel: `${monthLabel(prevStart)} - ${monthLabel(prevEnd)}`,
    };
  }, [startDate, endDate, compareStartDate, compareEndDate]);
  const productCategories = useMemo(() => processProducts(products, topProductsSort, activeBrandId), [products, topProductsSort, activeBrandId]);
  const categoryComparison = useMemo(
    () => processCategoryComparison(categoryRaw, startDate, endDate, compareStartDate, compareEndDate),
    [categoryRaw, startDate, endDate, compareStartDate, compareEndDate]
  );
  const productDropComparison = useMemo(
    () => processProductDropComparison(monthlyItemRaw, startDate, endDate, compareStartDate, compareEndDate),
    [monthlyItemRaw, startDate, endDate, compareStartDate, compareEndDate]
  );
  const ikasData = useMemo(() => processIkasData(ikasCurRows, ikasPrevRows), [ikasCurRows, ikasPrevRows]);
  const toggleChannelRow = useCallback((channelName) => {
    setExpandedChannelRows(prev => ({ ...prev, [channelName]: !prev[channelName] }));
  }, []);
  const togglePerfRow = useCallback((rowName) => {
    setExpandedPerfRows(prev => ({ ...prev, [rowName]: !prev[rowName] }));
  }, []);
  const togglePerfChannel = useCallback((key) => {
    setExpandedPerfChannels(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const onDateRangeChange = useCallback((range) => {
    const [s, e] = range || [];
    setPendingStartDate(s || null);
    setPendingEndDate(e || null);
    // Trigger data refresh only after both dates are selected.
    if (s && e) {
      setStartDate(s);
      setEndDate(e);
      if (isCompareAuto) {
        const autoCmp = getComparisonPeriods(s, e);
        setCompareStartDate(autoCmp.prevStart);
        setCompareEndDate(autoCmp.prevEnd);
        setPendingCompareStartDate(autoCmp.prevStart);
        setPendingCompareEndDate(autoCmp.prevEnd);
      }
    }
  }, [isCompareAuto]);
  const onCompareDateRangeChange = useCallback((range) => {
    const [s, e] = range || [];
    setPendingCompareStartDate(s || null);
    setPendingCompareEndDate(e || null);
    if (s && e) {
      setCompareStartDate(s);
      setCompareEndDate(e);
      setIsCompareAuto(false);
    }
  }, []);
  const resetCompareToAuto = useCallback(() => {
    const autoCmp = getComparisonPeriods(startDate, endDate);
    setCompareStartDate(autoCmp.prevStart);
    setCompareEndDate(autoCmp.prevEnd);
    setPendingCompareStartDate(autoCmp.prevStart);
    setPendingCompareEndDate(autoCmp.prevEnd);
    setIsCompareAuto(true);
  }, [startDate, endDate]);
  const toggleTopProductsSort = useCallback((key) => {
    setTopProductsSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { key, direction: 'desc' };
    });
  }, []);

  const exportExcel = useCallback(() => {
    const wb = XLSX.utils.book_new();

    // 1. GA Traffic Source – raw (date, source, medium, sessions, revenue, vb.)
    const gaRaw = gaSourceMediumRaw || [];
    const gaHeaders = gaRaw.length ? Object.keys(gaRaw[0]) : ['date', 'source', 'medium', 'sessions', 'totalRevenue', 'users', 'eventCount'];
    const gaRows = [gaHeaders, ...gaRaw.map((r) => gaHeaders.map((h) => r[h] ?? ''))];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(gaRows), 'GA Traffic Source');

    // 2. Unified Campaign Performance – raw (date, channel, campaign, cost, sessions, revenue, vb.)
    const chanRaw = channelRaw || [];
    const chanHeaders = chanRaw.length ? Object.keys(chanRaw[0]) : ['date', 'channel', 'campaign_name', 'ga4_sessions', 'ga4_total_revenue', 'google_cost', 'meta_cost'];
    const chanRows = [chanHeaders, ...chanRaw.map((r) => chanHeaders.map((h) => r[h] ?? ''))];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(chanRows), 'Kampanya Performans');

    // 3. Ürün Performansı – raw (itemId, itemName, itemCategory, totalItemsPurchased, totalRevenue)
    const prodRaw = products || [];
    const prodHeaders = prodRaw.length ? Object.keys(prodRaw[0]) : ['itemId', 'itemName', 'itemCategory', 'totalItemsPurchased', 'totalRevenue'];
    const prodRows = [prodHeaders, ...prodRaw.map((r) => prodHeaders.map((h) => r[h] ?? ''))];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(prodRows), 'Urun Performansi');

    // 4. Günlük Ürün Kırılımı – raw (date, itemId, itemName, itemCategory, totalRevenue, totalItemsViewed, totalItemsPurchased)
    const itemRaw = monthlyItemRaw || [];
    const itemHeaders = itemRaw.length ? Object.keys(itemRaw[0]) : ['date', 'itemId', 'itemName', 'itemCategory', 'totalRevenue', 'totalItemsViewed', 'totalItemsPurchased'];
    const itemRows = [itemHeaders, ...itemRaw.map((r) => itemHeaders.map((h) => r[h] ?? ''))];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(itemRows), 'Gunluk Urun Kirilimi');

    // 5. ikas Sipariş – raw (sadece günlük satırlar, toplam yok)
    if (hasIkasConnected) {
      const ikasRaw = [...(ikasCurRows || []).map((r) => ({ ...r, period: 'current' })), ...(ikasPrevRows || []).map((r) => ({ ...r, period: 'compare' }))];
      if (ikasRaw.length) {
        const ikasHeaders = Object.keys(ikasRaw[0]);
        const ikasRows = [ikasHeaders, ...ikasRaw.map((r) => ikasHeaders.map((h) => r[h] ?? ''))];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ikasRows), 'ikas Siparis');
      }
    }

    const filename = `aylik_performans_raw_${fmtDate(new Date())}.xlsx`;
    XLSX.writeFile(wb, filename);
  }, [gaSourceMediumRaw, channelRaw, products, monthlyItemRaw, ikasCurRows, ikasPrevRows, hasIkasConnected]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!showOnlyItemIdInTopProducts) {
        setItemNameById({});
        return;
      }
      const ids = Array.from(
        new Set(
          productCategories
            .flatMap(cat => cat.items || [])
            .map(item => String(item.itemId || item.item_id || '').trim())
            .filter(Boolean)
        )
      );
      if (!ids.length) {
        setItemNameById({});
        return;
      }

      try {
        const connRes = await fetch(`${API}/api/auth/connections`, {
          credentials: 'include',
          headers: hdr(activeBrandId),
        });
        if (!connRes.ok) return;
        const connData = await connRes.json().catch(() => ({}));
        let merchantId = '';
        if (Array.isArray(connData)) {
          const found = connData.find(x => (x?.platform === 'MERCHANT_CENTER') || (x?.platform === 'merchant_center')) || null;
          merchantId = found?.customerId || found?.merchantId || '';
        } else if (connData && typeof connData === 'object') {
          const mc = connData.MERCHANT_CENTER || connData.merchant_center || null;
          merchantId = mc?.customerId || mc?.merchantId || '';
        }
        if (!merchantId) return;

        const BATCH_SIZE = 50;
        const allMapped = {};
        for (let i = 0; i < ids.length; i += BATCH_SIZE) {
          if (cancelled) return;
          const batch = ids.slice(i, i + BATCH_SIZE);
          try {
            const r = await fetch(`${API}/api/google-ads/merchant-center/products/resolve-names`, {
              method: 'POST',
              credentials: 'include',
              headers: { ...hdr(activeBrandId), 'Content-Type': 'application/json' },
              body: JSON.stringify({ merchantId, itemIds: batch }),
            });
            if (r.ok) {
              const js = await r.json().catch(() => ({}));
              if (js?.data) Object.assign(allMapped, js.data);
            }
          } catch (_) { /* batch failed, continue with next */ }
        }

        if (cancelled) return;

        const unresolvedIds = ids.filter(id => !allMapped[id]);
        if (unresolvedIds.length > 0) {
          const fallbackResults = await Promise.all(
            unresolvedIds.map(async (id) => {
              try {
                const url = `${API}/api/google-ads/merchant-center/products/preview/db?merchantId=${encodeURIComponent(String(merchantId))}&limit=1&offset=0&q=${encodeURIComponent(id)}`;
                const r = await fetch(url, { credentials: 'include', headers: hdr(activeBrandId) });
                if (!r.ok) return [id, ''];
                const js = await r.json().catch(() => ({}));
                const first = Array.isArray(js?.data) ? js.data[0] : null;
                const title = first?.title ? String(first.title).trim() : '';
                return [id, title];
              } catch (_) {
                return [id, ''];
              }
            })
          );
          if (cancelled) return;
          fallbackResults.forEach(([id, title]) => {
            if (title) allMapped[id] = title;
          });
        }

        setItemNameById(allMapped);
      } catch (e) {
        console.error('Merchant Center item name mapping error:', e);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [showOnlyItemIdInTopProducts, productCategories, activeBrandId]);

  /* ── Render ────────────────────────────────────────────────────────── */

  return (
    <div className="min-h-screen pb-12 report3-excel" ref={pdfRef} style={{ zoom: 1.03 }}>
      <style>{`
        .report3-excel table.excel-grid {
          border-collapse: collapse !important;
        }
        .report3-excel table.excel-grid th {
          background: #64748b !important;
          color: #ffffff !important;
          border: 1px solid #94a3b8 !important;
          font-weight: 700 !important;
          letter-spacing: .02em;
        }
        .report3-excel table.excel-grid td {
          border: 1px solid #d1d5db !important;
          background: #ffffff !important;
          color: #334155 !important;
        }
      `}</style>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 tracking-tight">Aylık Performans Raporu</h1>
          <p className="text-[11px] text-slate-400 mt-0.5">Dijital kanallar, reklam performansı ve ürün satışları</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ConsistencyBadge startDate={startDate} endDate={endDate} />
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 shadow-sm">
            <Calendar size={12} className="text-slate-400" />
            <DatePicker
              selectsRange
              startDate={pendingStartDate}
              endDate={pendingEndDate}
              onChange={onDateRangeChange}
              dateFormat="dd MMM yyyy"
              className="text-[11px] text-slate-600 bg-transparent border-none outline-none w-[170px] cursor-pointer"
            />
          </div>
          <div className="relative">
            <button
              onClick={() => setShowComparePicker(v => !v)}
              className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] text-slate-600 hover:bg-slate-50 shadow-sm transition-colors"
            >
              Compare {isCompareAuto ? '(Auto)' : '(Custom)'}
            </button>
            {showComparePicker && (
              <div className="absolute right-0 mt-2 z-30 w-[300px] bg-white border border-slate-200 rounded-lg shadow-lg p-3">
                <div className="text-[10px] text-slate-500 mb-2">Compare period</div>
                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                  <Calendar size={12} className="text-slate-400" />
                  <DatePicker
                    selectsRange
                    startDate={pendingCompareStartDate}
                    endDate={pendingCompareEndDate}
                    onChange={onCompareDateRangeChange}
                    dateFormat="dd MMM yyyy"
                    className="text-[11px] text-slate-600 bg-transparent border-none outline-none w-[220px] cursor-pointer"
                  />
                </div>
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    onClick={() => setShowComparePicker(false)}
                    className="px-2.5 py-1 text-[10px] rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                  >
                    Close
                  </button>
                  {!isCompareAuto && (
                    <button
                      onClick={resetCompareToAuto}
                      className="px-2.5 py-1 text-[10px] rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                    >
                      Compare Auto
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <button onClick={fetchAll} disabled={loading} className="flex items-center gap-1.5 bg-slate-800 text-white rounded-lg px-3 py-1.5 text-[11px] font-medium hover:bg-slate-700 shadow-sm transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Yenile
          </button>
          <button
            onClick={exportExcel}
            className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] text-slate-600 hover:bg-slate-50 shadow-sm transition-colors"
          >
            <Download size={12} /> Excel
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg px-3 py-1.5 text-[11px] font-medium shadow-sm transition-colors"
          >
            {exporting ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
            {exporting ? 'PDF...' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Ekranı KAPATMAYAN ilerleme göstergesi. Eskiden tam ekran bir katman
          vardı ve en yavaş uç bitene kadar hazır tablolar da gizleniyordu. */}
      {pending.size > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
          <Loader2 size={12} className="animate-spin text-slate-500" />
          <span className="text-[11px] text-slate-600">
            Veriler yükleniyor — {TUM_ISLER.length - pending.size}/{TUM_ISLER.length} bölüm hazır
          </span>
          <div className="ml-1 h-1 flex-1 overflow-hidden rounded bg-slate-100">
            <div className="h-full rounded bg-slate-700 transition-all duration-300"
                 style={{ width: `${((TUM_ISLER.length - pending.size) / TUM_ISLER.length) * 100}%` }} />
          </div>
        </div>
      )}

      {/* 1. Dijital Kanallar - Özet */}
      <Section icon={Globe} title="Dijital Kanallar" busy={bekliyor('kanal')}>
        <div className="mb-2 text-[11px] text-slate-500">
          Karşılaştırma dönemi: <span className="font-medium text-slate-700">{channelMonthInfo.prevLabel}</span> vs <span className="font-medium text-slate-700">{channelMonthInfo.curLabel}</span>
        </div>
        {/* Toplam */}
        <div className="bg-white rounded-lg border border-slate-200/80 overflow-hidden mb-3">
          <table className="w-full border-collapse excel-grid">
            <thead>
              <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
                <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-400 uppercase tracking-wider" style={{ minWidth: 280 }}>Dijital Kanallar</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Trafik</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-medium text-slate-400 uppercase tracking-wider w-20">%</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Gelir</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-medium text-slate-400 uppercase tracking-wider w-20">%</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                <td className="px-3 py-2.5 text-[11px] font-semibold text-slate-800">Tüm Kanallar</td>
                <td className="px-3 py-2.5 text-right text-[11px] font-semibold text-slate-800 tabular-nums">{fmt(channelData.total.trafik, 'number')}</td>
                <td className="px-3 py-2.5 text-center"><PctBadge value={channelData.total.trafikPct} /></td>
                <td className="px-3 py-2.5 text-right text-[11px] font-semibold text-slate-800 tabular-nums">{fmt(channelData.total.gelir, 'currency')}</td>
                <td className="px-3 py-2.5 text-center"><PctBadge value={channelData.total.gelirPct} /></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Breakdown */}
        <div className="bg-white rounded-lg border border-slate-200/80 overflow-hidden">
          <table className="w-full border-collapse excel-grid">
            <thead>
              <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
                <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-400 uppercase tracking-wider" style={{ minWidth: 280 }}>Dijital Kanallar</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Trafik</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-medium text-slate-400 uppercase tracking-wider w-20">%</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Gelir</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-medium text-slate-400 uppercase tracking-wider w-20">%</th>
              </tr>
            </thead>
            <tbody>
              {channelData.channels.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-[11px] text-slate-400">Veri bulunamadı</td></tr>
              )}
              {channelData.channels.map((ch, i) => {
                const isOpen = !!expandedChannelRows[ch.name];
                return (
                  <React.Fragment key={ch.name}>
                    <tr className={`transition-colors duration-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'} hover:bg-blue-50/40`}>
                      <td className="px-3 py-2 text-[11px] text-slate-700 font-medium border-b border-slate-100/80">
                        <button type="button" onClick={() => toggleChannelRow(ch.name)} className="w-full flex items-center gap-2 text-left">
                          {isOpen ? <ChevronDown size={13} className="text-slate-500 flex-shrink-0" /> : <ChevronRight size={13} className="text-slate-500 flex-shrink-0" />}
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                          <span>{ch.name}</span>
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right text-[11px] text-slate-700 tabular-nums border-b border-slate-100/80">{fmt(ch.trafik, 'number')}</td>
                      <td className="px-3 py-2 text-center border-b border-slate-100/80"><PctBadge value={ch.trafikPct} /></td>
                      <td className="px-3 py-2 text-right text-[11px] text-slate-700 tabular-nums border-b border-slate-100/80">{fmt(ch.gelir, 'currency')}</td>
                      <td className="px-3 py-2 text-center border-b border-slate-100/80"><PctBadge value={ch.gelirPct} /></td>
                    </tr>
                    {isOpen && (
                      <tr className={`${i % 2 === 0 ? 'bg-slate-50/30' : 'bg-white'}`}>
                        <td colSpan={5} className="px-4 py-3 border-b border-slate-100/80">
                          <div className="rounded-md border border-slate-200/80 overflow-hidden">
                            <table className="w-full border-collapse excel-grid">
                              <thead>
                                <tr className="bg-slate-100/80">
                                  <th className="px-3 py-1.5 text-left text-[9px] font-medium text-slate-500 uppercase tracking-wider">Source / Medium</th>
                                  <th className="px-3 py-1.5 text-right text-[9px] font-medium text-slate-500 uppercase tracking-wider">Trafik</th>
                                  <th className="px-3 py-1.5 text-center text-[9px] font-medium text-slate-500 uppercase tracking-wider">%</th>
                                  <th className="px-3 py-1.5 text-right text-[9px] font-medium text-slate-500 uppercase tracking-wider">Gelir</th>
                                  <th className="px-3 py-1.5 text-center text-[9px] font-medium text-slate-500 uppercase tracking-wider">%</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ch.sourceMediums.length === 0 && (
                                  <tr>
                                    <td colSpan={5} className="px-3 py-3 text-center text-[10px] text-slate-400">Source/medium kırılımı bulunamadı</td>
                                  </tr>
                                )}
                                {ch.sourceMediums.map((s, idx2) => (
                                  <tr key={`${ch.name}-${s.name}-${idx2}`} className={idx2 % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}>
                                    <td className="px-3 py-1.5 text-[10px] text-slate-700 border-b border-slate-100/70">{s.name}</td>
                                    <td className="px-3 py-1.5 text-right text-[10px] text-slate-700 tabular-nums border-b border-slate-100/70">{fmt(s.trafik, 'number')}</td>
                                    <td className="px-3 py-1.5 text-center border-b border-slate-100/70"><PctBadge value={s.trafikPct} /></td>
                                    <td className="px-3 py-1.5 text-right text-[10px] text-slate-700 tabular-nums border-b border-slate-100/70">{fmt(s.gelir, 'currency')}</td>
                                    <td className="px-3 py-1.5 text-center border-b border-slate-100/70"><PctBadge value={s.gelirPct} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {/* 2. Performans vs Branding */}
      <Section icon={Megaphone} title="Kanallar" busy={bekliyor('perf', 'google', 'metaObj', 'mb', 'tiktok', 'planner')}>
        <div className="mb-2 text-[11px] text-slate-500">
          Karşılaştırma dönemi: <span className="font-medium text-slate-700">{channelMonthInfo.prevLabel}</span> vs <span className="font-medium text-slate-700">{channelMonthInfo.curLabel}</span>
        </div>
        <div className="bg-white rounded-lg border border-slate-200/80 overflow-hidden">
          <table className="w-full border-collapse excel-grid">
            <thead>
              <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
                <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-400 uppercase tracking-wider" style={{ minWidth: 200 }}>Kanallar</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Harcama</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-medium text-slate-400 uppercase tracking-wider w-20">%</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Trafik</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-medium text-slate-400 uppercase tracking-wider w-20">%</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Ciro</th>
                <th className="px-3 py-2.5 text-center text-[10px] font-medium text-slate-400 uppercase tracking-wider w-20">%</th>
              </tr>
            </thead>
            <tbody>
              {perfVsBrand.map((row, i) => {
                const isOpen = !!expandedPerfRows[row.name];
                return (
                  <React.Fragment key={row.name}>
                    <tr className={`transition-colors duration-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'} hover:bg-blue-50/40`}>
                      <td className="px-3 py-2.5 text-[11px] text-slate-700 font-medium border-b border-slate-100/80">
                        <button type="button" onClick={() => togglePerfRow(row.name)} className="w-full flex items-center gap-2 text-left">
                          {isOpen ? <ChevronDown size={13} className="text-slate-500 flex-shrink-0" /> : <ChevronRight size={13} className="text-slate-500 flex-shrink-0" />}
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                          <span>{row.name}</span>
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-right text-[11px] text-slate-700 tabular-nums border-b border-slate-100/80">{fmt(row.harcama, 'currency')}</td>
                      <td className="px-3 py-2.5 text-center border-b border-slate-100/80"><PctBadge value={row.harcamaPct} /></td>
                      <td className="px-3 py-2.5 text-right text-[11px] text-slate-700 tabular-nums border-b border-slate-100/80">{fmt(row.trafik, 'number')}</td>
                      <td className="px-3 py-2.5 text-center border-b border-slate-100/80"><PctBadge value={row.trafikPct} /></td>
                      <td className="px-3 py-2.5 text-right text-[11px] text-slate-700 tabular-nums border-b border-slate-100/80">{fmt(row.ciro, 'currency')}</td>
                      <td className="px-3 py-2.5 text-center border-b border-slate-100/80"><PctBadge value={row.ciroPct} /></td>
                    </tr>
                    {isOpen && (
                      <tr className={`${i % 2 === 0 ? 'bg-slate-50/30' : 'bg-white'}`}>
                        <td colSpan={7} className="px-4 py-3 border-b border-slate-100/80">
                          <div className="rounded-md border border-slate-200/80 overflow-hidden">
                            <table className="w-full border-collapse excel-grid">
                              <thead>
                                <tr className="bg-slate-100/80">
                                  <th className="px-3 py-1.5 text-left text-[9px] font-medium text-slate-500 uppercase tracking-wider">Kanal / Kampanya</th>
                                  <th className="px-3 py-1.5 text-right text-[9px] font-medium text-slate-500 uppercase tracking-wider">Harcama</th>
                                  <th className="px-3 py-1.5 text-center text-[9px] font-medium text-slate-500 uppercase tracking-wider">%</th>
                                  <th className="px-3 py-1.5 text-right text-[9px] font-medium text-slate-500 uppercase tracking-wider">Trafik</th>
                                  <th className="px-3 py-1.5 text-center text-[9px] font-medium text-slate-500 uppercase tracking-wider">%</th>
                                  <th className="px-3 py-1.5 text-right text-[9px] font-medium text-slate-500 uppercase tracking-wider">Ciro</th>
                                  <th className="px-3 py-1.5 text-center text-[9px] font-medium text-slate-500 uppercase tracking-wider">%</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(row.channelGroups || []).length === 0 && (
                                  <tr>
                                    <td colSpan={7} className="px-3 py-3 text-center text-[10px] text-slate-400">Kampanya verisi bulunamadı</td>
                                  </tr>
                                )}
                                {(row.channelGroups || []).map((g) => {
                                  const chKey = `${row.name}::${g.platform}`;
                                  const chOpen = !!expandedPerfChannels[chKey];
                                  return (
                                    <React.Fragment key={chKey}>
                                      {/* Kanal grubu — toplu satır (tıklayınca kampanyalar açılır) */}
                                      <tr className="bg-slate-50/70 hover:bg-blue-50/40">
                                        <td className="px-3 py-1.5 border-b border-slate-100/70">
                                          <button type="button" onClick={() => togglePerfChannel(chKey)} className="w-full flex items-center gap-2 text-left">
                                            {chOpen ? <ChevronDown size={12} className="text-slate-500 flex-shrink-0" /> : <ChevronRight size={12} className="text-slate-500 flex-shrink-0" />}
                                            <PlatformBadge platform={g.platform} />
                                            <span className="text-[9px] text-slate-400">{g.campaigns.length} kampanya</span>
                                          </button>
                                        </td>
                                        <td className="px-3 py-1.5 text-right text-[10px] font-semibold text-slate-800 tabular-nums border-b border-slate-100/70">{fmt(g.harcama, 'currency')}</td>
                                        <td className="px-3 py-1.5 text-center border-b border-slate-100/70"><PctBadge value={g.harcamaPct} /></td>
                                        <td className="px-3 py-1.5 text-right text-[10px] font-semibold text-slate-800 tabular-nums border-b border-slate-100/70">{fmt(g.trafik, 'number')}</td>
                                        <td className="px-3 py-1.5 text-center border-b border-slate-100/70"><PctBadge value={g.trafikPct} /></td>
                                        <td className="px-3 py-1.5 text-right text-[10px] font-semibold text-slate-800 tabular-nums border-b border-slate-100/70">{fmt(g.ciro, 'currency')}</td>
                                        <td className="px-3 py-1.5 text-center border-b border-slate-100/70"><PctBadge value={g.ciroPct} /></td>
                                      </tr>
                                      {/* Kanal açıkken → alt kampanyalar (girintili) */}
                                      {chOpen && g.campaigns.map((c, idx2) => (
                                        <tr key={`${chKey}-${c.name}-${idx2}`} className={idx2 % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}>
                                          <td className="pl-9 pr-3 py-1.5 text-[10px] text-slate-600 border-b border-slate-100/70">{c.displayName || c.name}</td>
                                          <td className="px-3 py-1.5 text-right text-[10px] text-slate-700 tabular-nums border-b border-slate-100/70">{fmt(c.cur.harcama, 'currency')}</td>
                                          <td className="px-3 py-1.5 text-center border-b border-slate-100/70"><PctBadge value={c.pct.harcama} /></td>
                                          <td className="px-3 py-1.5 text-right text-[10px] text-slate-700 tabular-nums border-b border-slate-100/70">{fmt(c.cur.trafik, 'number')}</td>
                                          <td className="px-3 py-1.5 text-center border-b border-slate-100/70"><PctBadge value={c.pct.trafik} /></td>
                                          <td className="px-3 py-1.5 text-right text-[10px] text-slate-700 tabular-nums border-b border-slate-100/70">{fmt(c.cur.ciro, 'currency')}</td>
                                          <td className="px-3 py-1.5 text-center border-b border-slate-100/70"><PctBadge value={c.pct.ciro} /></td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {/* 3. En Çok Satan Ürünler */}
      <Section icon={ShoppingBag} title="En Çok Satan Ürünler (Adet)" busy={bekliyor('urun')}>
        {productCategories.length === 0 && (
          <div className="bg-white rounded-lg border border-slate-200/80 px-3 py-6 text-center text-[11px] text-slate-400">Ürün verisi bulunamadı</div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {productCategories.map(cat => (
            <div key={cat.category} className="bg-white rounded-lg border border-slate-200/80 overflow-hidden">
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-3 py-2">
                <span className="text-[10px] font-medium text-slate-300 uppercase tracking-wider">{cat.category}</span>
              </div>
              <table className="w-full border-collapse table-fixed excel-grid">
                <colgroup>
                  <col style={{ width: '62%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '24%' }} />
                </colgroup>
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-3 py-1.5 text-left text-[9px] font-medium text-slate-400 uppercase tracking-wider">Ürün Adı</th>
                    <th className="px-2 py-1.5 text-right text-[9px] font-medium uppercase tracking-wider border-l border-slate-200/80">
                      <button
                        type="button"
                        onClick={() => toggleTopProductsSort('adet')}
                        className={`inline-flex items-center gap-1 transition-colors ${topProductsSort.key === 'adet' ? 'text-slate-700' : 'text-slate-400 hover:text-slate-600'}`}
                        title="Adete göre sırala"
                      >
                        Adet
                        {topProductsSort.key === 'adet' && (
                          topProductsSort.direction === 'desc'
                            ? <ChevronDown size={11} />
                            : <ChevronUp size={11} />
                        )}
                      </button>
                    </th>
                    <th className="px-3 py-1.5 text-right text-[9px] font-medium uppercase tracking-wider border-l border-slate-200/80">
                      <button
                        type="button"
                        onClick={() => toggleTopProductsSort('gelir')}
                        className={`inline-flex items-center gap-1 transition-colors ${topProductsSort.key === 'gelir' ? 'text-slate-700' : 'text-slate-400 hover:text-slate-600'}`}
                        title="Gelire göre sırala"
                      >
                        Gelir
                        {topProductsSort.key === 'gelir' && (
                          topProductsSort.direction === 'desc'
                            ? <ChevronDown size={11} />
                            : <ChevronUp size={11} />
                        )}
                      </button>
                    </th>
                  </tr>
                </thead>
              </table>
              <div className="h-[300px] overflow-y-auto [scrollbar-gutter:stable]">
                <table className="w-full border-collapse table-fixed excel-grid">
                <colgroup>
                  <col style={{ width: '62%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '24%' }} />
                </colgroup>
                <tbody>
                  {cat.items.map((item, i) => (
                    <tr key={item.itemId || i} className="hover:bg-blue-50/40 transition-colors">
                      <td className="px-3 py-2 text-[10px] text-slate-700 font-medium border-b border-slate-100/80 whitespace-normal break-words leading-tight max-w-[220px]" title={item.itemName}>
                        {showOnlyItemIdInTopProducts ? (
                          (() => {
                            const itemId = String(item.itemId || item.item_id || '').trim();
                            const resolvedName = itemNameById[itemId] || '';
                            const ga4Name = (item.itemName && item.itemName !== 'Unknown Item') ? item.itemName : '';
                            const displayName = resolvedName || ga4Name;
                            if (displayName) {
                              return (
                                <>
                                  <div className="text-[10px] text-slate-700">{displayName}</div>
                                  <div className="text-[9px] font-normal text-slate-400 mt-0.5">ID: {itemId}</div>
                                </>
                              );
                            }
                            return <div className="text-[9px] font-normal text-slate-500">ID: {itemId || '-'}</div>;
                          })()
                        ) : (
                          <>
                            <div className="text-[10px] text-slate-700">{item.itemName}</div>
                            <div className="text-[9px] font-normal text-slate-400 mt-0.5">
                              ID: {item.itemId || item.item_id || '-'}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right text-[11px] text-slate-600 tabular-nums border-b border-slate-100/80 border-l border-slate-200/70">
                        {fmt(item.totalItemsPurchased, 'number')}
                      </td>
                      <td className="px-3 py-2 text-right text-[11px] text-slate-600 tabular-nums border-b border-slate-100/80 border-l border-slate-200/70">
                        {fmt(item.totalRevenue, 'currency')}
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 4. Kategori Performans Karşılaştırması */}
      <Section icon={BarChart3} title="Kategori Performans Karşılaştırması" busy={bekliyor('gunluk')}>
        <div className="bg-white rounded-lg border border-slate-200/80 overflow-hidden">
          <div className="max-h-[860px] overflow-y-auto overflow-x-auto">
            <table className="w-full border-collapse excel-grid">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
                  <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-400 uppercase tracking-wider" style={{ minWidth: 160 }}>Kategori</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-400 uppercase tracking-wider" style={{ minWidth: 180 }}>Tarih</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Ürün Görüntüleme</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Sepete Ekleme</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Satın Alma</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Gelir</th>
                </tr>
              </thead>
              <tbody>
                {categoryComparison.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-[11px] text-slate-400">Veri bulunamadı</td></tr>
                )}
                {categoryComparison.map((row, idx) => {
                  const isTotal = row.category === 'TOPLAM';
                  const borderClass = 'border-b-2 border-slate-200';
                  return (
                    <React.Fragment key={row.category}>
                      {/* Current Period */}
                      <tr className={`${isTotal ? 'bg-slate-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                        <td rowSpan={3} className={`px-3 py-2 text-[11px] ${isTotal ? 'font-bold text-slate-800' : 'font-medium text-slate-700'} align-middle ${borderClass}`}>
                          {row.category}
                        </td>
                        <td className="px-3 py-1.5 text-[10px] text-slate-500 border-b border-slate-100/60">
                          {row.curLabel}
                        </td>
                        <td className="px-3 py-1.5 text-right text-[11px] text-slate-700 tabular-nums border-b border-slate-100/60 font-medium">
                          {fmt(row.cur.views, 'number')}
                        </td>
                        <td className="px-3 py-1.5 text-right text-[11px] text-slate-700 tabular-nums border-b border-slate-100/60 font-medium">
                          {fmt(row.cur.addToCart, 'number')}
                        </td>
                        <td className="px-3 py-1.5 text-right text-[11px] text-slate-700 tabular-nums border-b border-slate-100/60 font-medium">
                          {fmt(row.cur.purchases, 'number')}
                        </td>
                        <td className="px-3 py-1.5 text-right text-[11px] text-slate-700 tabular-nums border-b border-slate-100/60 font-medium">
                          {fmt(row.cur.revenue, 'currency')}
                        </td>
                      </tr>
                      {/* Previous Period */}
                      <tr className={`${isTotal ? 'bg-slate-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                        <td className="px-3 py-1.5 text-[10px] text-slate-400 border-b border-slate-100/60">
                          {row.prevLabel}
                        </td>
                        <td className="px-3 py-1.5 text-right text-[11px] text-slate-500 tabular-nums border-b border-slate-100/60">
                          {fmt(row.prev.views, 'number')}
                        </td>
                        <td className="px-3 py-1.5 text-right text-[11px] text-slate-500 tabular-nums border-b border-slate-100/60">
                          {fmt(row.prev.addToCart, 'number')}
                        </td>
                        <td className="px-3 py-1.5 text-right text-[11px] text-slate-500 tabular-nums border-b border-slate-100/60">
                          {fmt(row.prev.purchases, 'number')}
                        </td>
                        <td className="px-3 py-1.5 text-right text-[11px] text-slate-500 tabular-nums border-b border-slate-100/60">
                          {fmt(row.prev.revenue, 'currency')}
                        </td>
                      </tr>
                      {/* % Change */}
                      <tr className={`${isTotal ? 'bg-slate-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                        <td className={`px-3 py-1.5 text-[10px] text-slate-400 italic ${borderClass}`}>
                          % change
                        </td>
                        <td className={`px-3 py-1.5 text-right ${borderClass}`}>
                          <CatPctCell value={row.pct.views} />
                        </td>
                        <td className={`px-3 py-1.5 text-right ${borderClass}`}>
                          <CatPctCell value={row.pct.addToCart} />
                        </td>
                        <td className={`px-3 py-1.5 text-right ${borderClass}`}>
                          <CatPctCell value={row.pct.purchases} />
                        </td>
                        <td className={`px-3 py-1.5 text-right ${borderClass}`}>
                          <CatPctCell value={row.pct.revenue} />
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* 4.1 En çok düşüş yaşayan ürünler (aylık karşılaştırma) */}
      <Section icon={TrendingDown} title="En Çok Düşüş Yaşayan Ürünler (Adet)" busy={bekliyor('gunluk')}>
        <div className="bg-white rounded-lg border border-slate-200/80 overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50/70 text-[11px] text-slate-600">
            <span className="font-medium">{productDropComparison.prevLabel}</span> vs{' '}
            <span className="font-medium">{productDropComparison.curLabel}</span> (en çok adet kaybeden ürünler)
          </div>
          <div className="max-h-[440px] overflow-y-auto overflow-x-auto">
            <table className="w-full border-collapse excel-grid">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
                  <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-400 uppercase tracking-wider" style={{ minWidth: 260 }}>Ürün</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-400 uppercase tracking-wider" style={{ minWidth: 180 }}>Kategori</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">{productDropComparison.prevLabel} (Adet)</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">{productDropComparison.curLabel} (Adet)</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-medium text-slate-400 uppercase tracking-wider w-24">Düşüş (Adet)</th>
                </tr>
              </thead>
              <tbody>
                {productDropComparison.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-[11px] text-slate-400">Karşılaştırma için yeterli ürün verisi bulunamadı</td>
                  </tr>
                )}
                {productDropComparison.items.map((item, idx) => (
                  <tr key={`${item.itemName}-${idx}`} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'} hover:bg-red-50/30`}>
                    <td className="px-3 py-2 text-[11px] text-slate-700 font-medium border-b border-slate-100/80">
                      <div className="text-[11px] text-slate-700">{item.itemName}</div>
                      <div className="text-[9px] font-normal text-slate-400 mt-0.5">
                        ID: {item.itemId || '-'}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-600 border-b border-slate-100/80">{item.itemCategory}</td>
                    <td className="px-3 py-2 text-right text-[11px] text-slate-500 tabular-nums border-b border-slate-100/80">{fmt(item.prev.purchased, 'number')}</td>
                    <td className="px-3 py-2 text-right text-[11px] text-slate-700 tabular-nums border-b border-slate-100/80">{fmt(item.cur.purchased, 'number')}</td>
                    <td className="px-3 py-2 text-center border-b border-slate-100/80">
                      <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums bg-red-50 text-red-600">
                        -{fmt(item.absDropped, 'number')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {productDropComparison.items.length > 10 && (
            <div className="px-3 py-2 text-[10px] text-slate-500 border-t border-slate-100 bg-slate-50/60">
              İlk 10 ürün görünür alana sığacak şekilde kart içi scroll aktiftir. Toplam {productDropComparison.items.length} ürün.
            </div>
          )}
        </div>
      </Section>

      {/* 5. ikas Sipariş Özeti (sadece bağlı markalarda) */}
      {hasIkasConnected && (
        <Section icon={Store} title="ikas Sipariş Özeti" busy={bekliyor('ikas')}>
          {ikasLoading ? (
            <div className="bg-white rounded-lg border border-slate-200/80 px-3 py-8 text-center">
              <Loader2 size={16} className="text-slate-400 animate-spin mx-auto" />
              <p className="text-[11px] text-slate-400 mt-2">ikas verileri yükleniyor…</p>
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  { label: 'Gelir', cur: ikasData.cur.revenue, pct: ikasData.pct.revenue, format: 'currency' },
                  { label: 'Sipariş', cur: ikasData.cur.orders, pct: ikasData.pct.orders, format: 'number' },
                  { label: 'Ürün Adedi', cur: ikasData.cur.items, pct: ikasData.pct.items, format: 'number' },
                ].map(kpi => (
                  <div key={kpi.label} className="bg-white rounded-lg border border-slate-200/80 p-3">
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">{kpi.label}</div>
                    <div className="text-lg font-bold text-slate-800 tabular-nums">{fmt(kpi.cur, kpi.format)}</div>
                    <div className="mt-1"><PctBadge value={kpi.pct} /></div>
                  </div>
                ))}
              </div>

              {/* Daily Breakdown */}
              <div className="bg-white rounded-lg border border-slate-200/80 overflow-hidden">
                <table className="w-full border-collapse excel-grid">
                  <thead>
                    <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
                      <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-400 uppercase tracking-wider">Tarih</th>
                      <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Gelir</th>
                      <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Sipariş</th>
                      <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider">Ürün Adedi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ikasData.dailyRows.length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-6 text-center text-[11px] text-slate-400">Günlük veri bulunamadı</td></tr>
                    )}
                    {ikasData.dailyRows.map((r, i) => (
                      <tr key={r.date || i} className={`transition-colors duration-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'} hover:bg-blue-50/40`}>
                        <td className="px-3 py-2 text-[11px] text-slate-700 font-medium border-b border-slate-100/80">
                          {r.date ? new Date(r.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }) : '–'}
                        </td>
                        <td className="px-3 py-2 text-right text-[11px] text-emerald-700 font-medium tabular-nums border-b border-slate-100/80">
                          {fmt(r.revenue, 'currency')}
                        </td>
                        <td className="px-3 py-2 text-right text-[11px] text-slate-700 tabular-nums border-b border-slate-100/80">
                          {fmt(r.orders, 'number')}
                        </td>
                        <td className="px-3 py-2 text-right text-[11px] text-slate-700 tabular-nums border-b border-slate-100/80">
                          {fmt(r.items, 'number')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Section>
      )}
    </div>
  );
}
